import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';

interface BankAccount {
  id: number;
  bank_name: string;
  account_number: string;
  account_type: string;
  current_balance: string;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function BankAccounts() {
  const { idToken } = useCognitoAuth();
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    async function fetchAccounts() {
      try {
        const data = await api.get<BankAccount[]>('/api/entities/bank-accounts', { errorCode: ErrorCode.FETCH_BANK_ACCOUNTS, token: idToken! });
        setAccounts(data ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch bank accounts');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchAccounts();
  }, [retryKey, idToken]);

  const formatCurrency = (amount: number, currency = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const maskAccountNumber = (num: string) => {
    if (num.length <= 4) return num;
    return '••••' + num.slice(-4);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="animate-spin text-3xl mb-4"><i className="bi bi-arrow-repeat"></i></div>
        <p className="text-muted-foreground">Loading bank accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Bank Accounts</h1>
          <p className="text-sm text-muted-foreground">Connected bank accounts and balances</p>
        </div>
        <Button variant="default" size="sm">
          <i className="bi bi-plus"></i> Add Bank Account
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Cash</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency(accounts.reduce((sum, a) => sum + Number(a.current_balance), 0))}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Accounts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{accounts.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Active</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {accounts.filter(a => a.is_active).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {accounts.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Connected Accounts ({accounts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Bank</th>
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Account</th>
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Type</th>
                    <th className="py-3 px-4 text-right font-medium text-muted-foreground">Balance</th>
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Status</th>
                    <th className="py-3 px-4 text-left font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id} className="border-b border-border hover:bg-muted/50">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <i className="bi bi-bank"></i>
                          {account.bank_name}
                        </div>
                      </td>
                      <td className="py-3 px-4 font-mono">{maskAccountNumber(account.account_number)}</td>
                      <td className="py-3 px-4">{account.account_type}</td>
                      <td className="py-3 px-4 text-right font-mono">
                        {formatCurrency(Number(account.current_balance), account.currency)}
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={account.is_active ? 'secondary' : 'default'}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm">
                            <i className="bi bi-arrow-repeat"></i>
                          </Button>
                          <Button variant="ghost" size="sm">
                            <i className="bi bi-pencil"></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <div className="text-5xl text-muted-foreground mb-4">
                <i className="bi bi-bank"></i>
              </div>
              <h3 className="text-lg font-semibold mb-2">No bank accounts</h3>
              <p className="text-muted-foreground mb-4">
                Connect a bank account to import transactions automatically.
              </p>
              <Button variant="default">
                <i className="bi bi-plus"></i> Add Bank Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <i className="bi bi-shield-check"></i> Bank Connection Security
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            Bank connections use secure OAuth authentication. Your credentials are never stored.
            Connections are read-only and can be revoked at any time.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
